import React, { useState } from "react";
import { FaSearch } from "react-icons/fa";
import PageLayout from "../../../components/layout/PageLayout";
import { Header } from "../../../components/ui";
import api from "../../../services/api";

import { FIELD_OPTIONS } from "./fieldOptions";
import EntityTypeTabs from "./components/EntityTypeTabs";
import FieldSelect from "./components/FieldSelect";
import ResultCard from "./components/ResultCard/ResultCard";

export default function DebugSearchPage() {
  const [entityType, setEntityType] = useState("route");
  const [field, setField] = useState("any");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copyInfo, setCopyInfo] = useState(null); // тост "Скопировано"

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    if (!query.trim()) return;

    setLoading(true);
    setExpandedId(null);

    const fieldForBackend = field || "any";

    const body = {
      type: entityType,
      field: fieldForBackend,
      query,
      limit: 100,
    };

    try {
      const response = await api.post("/routes/debug", body);
      const payload = response?.data ?? response;
      const items = payload.results || [];
      setResults(items);
    } catch (err) {
      console.error(err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text) => {
    if (!text || text === "—") return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopyInfo(`Скопировано: ${text}`);
      setTimeout(() => setCopyInfo(null), 1500);
    } catch (err) {
      console.error("Ошибка копирования", err);
    }
  };

  return (
    <PageLayout
      header={
        <Header
          icon={FaSearch}
          title="Глобальный поиск для отладки"
          subtitle="Маршруты, квитанции и товары по любому полю (до 100 результатов)"
        />
      }
    >
      {/* Верхняя панель поиска */}
      <div
        style={{
          borderRadius: "0.9rem",
          border: "1px solid #e2e8f0",
          padding: "0.9rem",
          background: "#ffffff",
          marginBottom: "1rem",
          boxShadow: "0 1px 4px rgba(15, 23, 42, 0.04)",
        }}
      >
        <EntityTypeTabs entityType={entityType} setEntityType={setEntityType} />

        <form
          onSubmit={handleSearch}
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(160px, 200px) 1fr auto",
            gap: "0.75rem",
            alignItems: "flex-end",
          }}
        >
          <FieldSelect entityType={entityType} field={field} setField={setField} />

          <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <label style={{ fontSize: "0.8rem", color: "#64748b" }}>Значение</label>
            <input
              type="text"
              placeholder="Введите значение для поиска (по вхождению)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{
                padding: "0.45rem 0.6rem",
                borderRadius: "0.5rem",
                border: "1px solid #e2e8f0",
                fontSize: "0.9rem",
                outline: "none",
              }}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.4rem",
              padding: "0.55rem 0.9rem",
              borderRadius: "0.6rem",
              border: "none",
              cursor: "pointer",
              background: "#1d4ed8",
              color: "#ffffff",
              fontWeight: 500,
              fontSize: "0.9rem",
              minWidth: "120px",
            }}
          >
            <FaSearch />
            {loading ? "Поиск..." : "Найти"}
          </button>
        </form>

        {/* summary */}
        <div
          style={{
            marginTop: "0.6rem",
            fontSize: "0.8rem",
            color: "#64748b",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span>
            Тип:{" "}
            <strong>
              {entityType === "route"
                ? "Маршруты"
                : entityType === "receipt"
                ? "Квитанции"
                : "Товары"}
            </strong>{" "}
            · Поле:{" "}
            <strong>
              {
                (FIELD_OPTIONS[entityType] || []).find((f) => f.value === field)
                  ?.label
              }
            </strong>
          </span>
          <span>Лимит результатов: 100</span>
        </div>
      </div>

      {/* Результаты */}
      {loading ? (
        <p>Загрузка результатов поиска...</p>
      ) : results.length === 0 ? (
        <p>Ничего не найдено. Введите параметры и нажмите &quot;Найти&quot;.</p>
      ) : (
        <>
          <p style={{ marginBottom: "0.5rem", fontSize: "0.85rem", color: "#64748b" }}>
            Найдено: {results.length}
            {results.length === 100 && " (показаны только первые 100)"}
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
              gap: "0.9rem",
              marginBottom: "1rem",
            }}
          >
            {results.map((item, index) => {
              const safeId =
                item.id ||
                item.receipt_id ||
                item.product_id ||
                `${entityType}-${index}`;

              return (
                <ResultCard
                  key={safeId}
                  item={item}
                  entityType={entityType}
                  isExpanded={expandedId === safeId}
                  toggleExpand={() =>
                    setExpandedId((prev) => (prev === safeId ? null : safeId))
                  }
                  onCopy={handleCopy}
                />
              );
            })}
          </div>
        </>
      )}

      {/* маленький тост "Скопировано" */}
      {copyInfo && (
        <div
          style={{
            position: "fixed",
            bottom: "1.25rem",
            right: "1.25rem",
            background: "#0f172a",
            color: "#f9fafb",
            padding: "0.4rem 0.75rem",
            borderRadius: "999px",
            fontSize: "0.8rem",
            boxShadow: "0 8px 20px rgba(15,23,42,0.35)",
            zIndex: 50,
          }}
        >
          {copyInfo}
        </div>
      )}
    </PageLayout>
  );
}
